import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../routes";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { WorthApi } from "../api/WorthApi";
import { Content } from "../components/Content";
import { ScrollView } from "react-native-gesture-handler";

const STORAGE_KEY = "vesting:grants";

type Grant = {
  id: string;
  grantDate: string; // ISO yyyy-mm-dd
  shares: number;
  symbol?: string;
  deferralYears: number;
  vestedOverYears: number;
  notes?: string;
};

interface AggregatedPayout {
  date: Date;
  shares: number;
  value: number;
  nonDeferredShares: number;
  nonDeferredValue: number;
  deferralCount: number;
  deferralShares: number;
  deferralValue: number;
}

function parseDateIso(s: string) {
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d;
}

function formatDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function formatDisplayDate(d: Date | null) {
  if (!d) return "";
  try {
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
}

function generatePayDates(grantDateIso: string, vestedOverYears: number) {
  const grant = parseDateIso(grantDateIso);
  if (!grant) return [] as Date[];
  const end = new Date(grant);
  end.setFullYear(end.getFullYear() + vestedOverYears);

  const months = [1, 4, 7, 10]; // Feb, May, Aug, Nov (0-based)
  const result: Date[] = [];
  for (let y = grant.getFullYear(); y <= end.getFullYear(); y++) {
    months.forEach((m) => {
      const d = new Date(Date.UTC(y, m, 15));
      if (d >= grant && d <= end) result.push(d);
    });
  }
  result.sort((a, b) => a.getTime() - b.getTime());
  return result;
}

function getDeferralEnd(grant: Grant) {
  const grantDate = parseDateIso(grant.grantDate);
  if (!grantDate) return null;

  const deferralEnd = new Date(
    Date.UTC(
      grantDate.getUTCFullYear() +
        (grant.deferralYears ?? grant.vestedOverYears ?? 3),
      grantDate.getUTCMonth(),
      grantDate.getUTCDate(),
    ),
  );
  // The anniversary itself is still part of the deferral period.
  deferralEnd.setUTCDate(deferralEnd.getUTCDate() + 1);
  return deferralEnd;
}

export default function Vesting() {
  const [grants, setGrants] = useState<Grant[]>([]);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [priceMap, setPriceMap] = useState<
    Record<string, { price: number; currency?: string }>
  >({});

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const res = await WorthApi.get("/vesting/grants");
      if (Array.isArray(res)) {
        const mapped: Grant[] = res.map((r: any) => ({
          id: r._id,
          grantDate: r.grantDate ? r.grantDate.slice(0, 10) : "",
          shares: r.shares,
          symbol: r.symbol,
          deferralYears: r.deferralYears,
          vestedOverYears: r.vestedOverYears ?? r.deferralYears ?? 3,
          notes: r.notes,
        }));
        setGrants(mapped);
        return;
      }
    } catch (e) {
      console.warn("Failed to load grants from API, falling back to local", e);
    }

    try {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      if (raw) setGrants(JSON.parse(raw));
    } catch (e) {
      console.warn("Failed to load grants from storage", e);
    }
  }

  async function save(list: Grant[]) {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn("Failed to save grants to storage", e);
    }
    setGrants(list);
  }

  function removeGrant(id: string) {
    WorthApi.delete(`/vesting/grants/${id}`)
      .then(() => {
        const next = grants.filter((g) => g.id !== id);
        save(next);
      })
      .catch((e) => {
        console.warn("Failed to delete grant on API, removing locally", e);
        const next = grants.filter((g) => g.id !== id);
        save(next);
      });
  }

  useEffect(() => {
    const symbols = Array.from(
      new Set(grants.map((g) => g.symbol || "").filter(Boolean)),
    );
    if (symbols.length === 0) return;
    let mounted = true;
    Promise.all(
      symbols.map((s) =>
        WorthApi.getStock(s).then((res) => ({ symbol: s, res })),
      ),
    )
      .then((rows) => {
        if (!mounted) return;
        const m: Record<string, { price: number; currency?: string }> = {};
        rows.forEach(({ symbol, res }) => {
          if (res && res.regularMarketPrice != null)
            m[symbol] = {
              price: res.regularMarketPrice,
              currency: res.currency,
            };
        });
        setPriceMap(m);
      })
      .catch((e) => console.warn("Failed to fetch stock prices", e));
    return () => {
      mounted = false;
    };
  }, [grants]);

  // build aggregated rows
  const aggregatedRows = (() => {
    if (grants.length === 0) return [] as AggregatedPayout[];
    const map: Record<string, AggregatedPayout> = {};
    grants.forEach((g) => {
      const dates = generatePayDates(
        g.grantDate,
        g.vestedOverYears || g.deferralYears,
      );
      const payments = dates.length;
      const base = Math.floor(g.shares / Math.max(1, payments));
      const rem = g.shares % Math.max(1, payments);
      const perGrantAlloc: { iso: string; date: Date; alloc: number }[] = [];
      const deferralEnd = getDeferralEnd(g);
      // balanced distribution of remainder across payments
      const extras = new Set<number>();
      if (rem > 0) {
        for (let j = 0; j < rem; j++)
          extras.add(Math.floor((j * payments) / rem));
      }
      dates.forEach((d, idx) => {
        const iso = d.toISOString().slice(0, 10);
        const alloc = base + (extras.has(idx) ? 1 : 0);
        perGrantAlloc.push({ iso, date: d, alloc });
        if (!map[iso])
          map[iso] = {
            date: d,
            shares: 0,
            value: 0,
            nonDeferredShares: 0,
            nonDeferredValue: 0,
            deferralCount: 0,
            deferralShares: 0,
            deferralValue: 0,
          };
        map[iso].shares += alloc;
        const price = g.symbol ? priceMap[g.symbol]?.price || 0 : 0;
        map[iso].value += alloc * price;
        if (deferralEnd && d.getTime() > deferralEnd.getTime()) {
          map[iso].nonDeferredShares += alloc;
          map[iso].nonDeferredValue += alloc * price;
        }
      });

      try {
        if (deferralEnd) {
          const iso = deferralEnd.toISOString().slice(0, 10);
          const cum = perGrantAlloc
            .filter((p) => p.iso <= iso)
            .reduce((s, p) => s + p.alloc, 0);
          if (!map[iso])
            map[iso] = {
              date: deferralEnd,
              shares: 0,
              value: 0,
              nonDeferredShares: 0,
              nonDeferredValue: 0,
              deferralCount: 0,
              deferralShares: 0,
              deferralValue: 0,
            };
          map[iso].deferralCount += 1;
          map[iso].deferralShares += cum;
          if (g.symbol && priceMap[g.symbol])
            map[iso].deferralValue += cum * (priceMap[g.symbol].price || 0);
        }
      } catch (e) {
        // noop
      }
    });

    const allDates = Object.values(map).sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );
    // Only include upcoming (future) payout dates in the "Upcoming payouts" list
    const now = new Date();
    const futureOnly = allDates.filter(
      (d) => d.date.getTime() >= now.getTime(),
    );
    return futureOnly;
  })();

  // totals for display: unvested (future scheduled) and deferred (deferral ends)
  const totals = (() => {
    const unvestedShares = aggregatedRows.reduce(
      (s, r) => s + (r.shares || 0),
      0,
    );
    const unvestedValue = aggregatedRows.reduce(
      (s, r) => s + (r.value || 0),
      0,
    );

    // deferred as of today = sum of allocations that are scheduled between now and each grant's deferral end
    const now = new Date();
    let deferredShares = 0;
    let deferredValue = 0;
    grants.forEach((g) => {
      const dates = generatePayDates(
        g.grantDate,
        g.vestedOverYears || g.deferralYears,
      );
      const payments = dates.length || 1;
      const base = Math.floor(g.shares / Math.max(1, payments));
      const rem = g.shares % Math.max(1, payments);
      const extras = new Set<number>();
      if (rem > 0) {
        for (let j = 0; j < rem; j++)
          extras.add(Math.floor((j * payments) / rem));
      }
      const defEnd = getDeferralEnd(g);
      if (!defEnd) return;
      dates.forEach((d, idx) => {
        // vested but not yet paid: vest date is on or before today, but deferral end is in the future
        if (d.getTime() <= now.getTime() && defEnd.getTime() > now.getTime()) {
          const alloc = base + (extras.has(idx) ? 1 : 0);
          deferredShares += alloc;
          if (g.symbol && priceMap[g.symbol])
            deferredValue += alloc * (priceMap[g.symbol].price || 0);
        }
      });
    });

    return { unvestedShares, unvestedValue, deferredShares, deferredValue };
  })();

  return (
    <View style={[styles.container]}>
      <Content>
        <ScrollView
          style={{ backgroundColor: "#201D2B" }}
          contentContainerStyle={{ backgroundColor: "#201D2B" }}
        >
          <Text style={styles.title}>Vesting Schedules</Text>

          {(totals.unvestedShares > 0 || totals.deferredShares > 0) && (
            <View style={{ marginBottom: 12 }}>
              <View style={styles.totalsRow}>
                <View style={styles.totalCard}>
                  <Text style={styles.totalLabel}>Unvested</Text>
                  {totals.unvestedValue > 0 ? (
                    <>
                      <Text style={styles.totalValue}>
                        {new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency:
                            Object.values(priceMap)[0]?.currency || "USD",
                        }).format(totals.unvestedValue)}
                      </Text>
                      <Text style={styles.small}>
                        {totals.unvestedShares} shares
                      </Text>
                    </>
                  ) : (
                    <Text style={styles.totalNumber}>
                      {totals.unvestedShares} shares
                    </Text>
                  )}
                </View>
                <View style={[styles.totalCard, { marginLeft: 8 }]}>
                  <Text style={styles.totalLabel}>Deferred</Text>
                  {totals.deferredValue > 0 ? (
                    <>
                      <Text style={[styles.totalValue, styles.deferralGreen]}>
                        {new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency:
                            Object.values(priceMap)[0]?.currency || "USD",
                        }).format(totals.deferredValue)}
                      </Text>
                      <Text style={[styles.small, styles.deferralGreen]}>
                        {totals.deferredShares} shares
                      </Text>
                    </>
                  ) : (
                    <Text style={[styles.totalNumber, styles.deferralGreen]}>
                      {totals.deferredShares} shares
                    </Text>
                  )}
                </View>
              </View>
            </View>
          )}

          {aggregatedRows.length > 0 && (
            <View style={{ marginBottom: 12 }}>
              <Text style={{ color: "#ddd", fontSize: 14, marginBottom: 8 }}>
                Upcoming payouts
              </Text>
              <FlatList
                data={aggregatedRows}
                keyExtractor={(r) => r.date.toISOString()}
                renderItem={({ item }) => {
                  const currency = Object.values(priceMap)[0]?.currency;
                  const formattedValue = item.value
                    ? currency
                      ? new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency,
                        }).format(item.value)
                      : `${item.value.toFixed(2)}`
                    : "";
                  const formattedDeferralValue = item.deferralValue
                    ? currency
                      ? new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency,
                        }).format(item.deferralValue)
                      : `${item.deferralValue.toFixed(2)}`
                    : "";
                  const formattedNonDeferredValue = item.nonDeferredValue
                    ? currency
                      ? new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency,
                        }).format(item.nonDeferredValue)
                      : `${item.nonDeferredValue.toFixed(2)}`
                    : "";
                  const isDeferral = item.deferralCount > 0;
                  return (
                    <View style={styles.payoutCard}>
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <View>
                          <Text style={styles.payoutDate}>
                            {formatDisplayDate(item.date)}
                          </Text>
                          {item.deferralCount > 0 && (
                            <Text style={styles.small}>
                              {item.deferralCount} deferral end
                            </Text>
                          )}
                        </View>
                        <View style={{ alignItems: "flex-end" }}>
                          <Text
                            style={[
                              styles.payoutValue,
                              isDeferral && styles.deferralGreen,
                            ]}
                          >
                            {formattedValue || formattedDeferralValue || ""}
                          </Text>
                          <Text
                            style={[
                              styles.payoutShares,
                              isDeferral && styles.deferralGreen,
                            ]}
                          >
                            {item.shares || item.deferralShares} shares
                          </Text>
                          {item.nonDeferredShares > 0 && (
                            <Text style={styles.nonDeferredIncome}>
                              Non-deferred income: {formattedNonDeferredValue
                                ? `${formattedNonDeferredValue} · ${item.nonDeferredShares} shares`
                                : `${item.nonDeferredShares} shares`}
                            </Text>
                          )}
                        </View>
                      </View>
                    </View>
                  );
                }}
              />
            </View>
          )}
          <Text style={styles.title}>Grants</Text>

          {grants.length === 0 ? (
            <Text style={{ color: "white" }}>
              No grants yet. Add one above.
            </Text>
          ) : (
            grants.map((g) => (
              <View key={g.id} style={styles.grantCard}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <Text style={styles.grantTitle}>
                    {g.shares} shares {g.symbol ? `· ${g.symbol}` : ""} —{" "}
                    {g.grantDate}
                  </Text>
                  <View style={{ flexDirection: "row" }}>
                    <TouchableOpacity onPress={() => removeGrant(g.id)}>
                      <Text style={{ color: "red" }}>Delete</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() =>
                        navigation.navigate("VestingEdit", { id: g.id })
                      }
                    >
                      <Text style={{ color: "lightblue", marginLeft: 12 }}>
                        Edit
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
                <Text style={styles.small}>
                  Vested over: {g.vestedOverYears ?? g.deferralYears} years
                </Text>
              </View>
            ))
          )}
        </ScrollView>
      </Content>

      <TouchableOpacity
        style={styles.addButton}
        onPress={() => navigation.navigate("VestingAdd")}
      >
        <Text style={styles.plusText}>+</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#201D2B" },
  title: { fontSize: 20, color: "white", marginVertical: 24 },
  grantCard: {
    padding: 12,
    borderWidth: 1,
    borderColor: "#333",
    marginBottom: 12,
    borderRadius: 8,
    backgroundColor: "#2A2735",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  grantTitle: { color: "white", fontWeight: "600" },
  small: { color: "#ddd", fontSize: 12 },
  payoutCard: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#2B2A3A",
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 3,
    elevation: 2,
  },
  payoutDate: { color: "#fff", fontWeight: "700", fontSize: 14 },
  payoutValue: { color: "#fff", fontWeight: "700", fontSize: 14 },
  payoutShares: { color: "#ddd", fontSize: 12 },
  deferralGreen: { color: "#2ECC71" },
  nonDeferredIncome: { color: "#9ED0FF", fontSize: 12, marginTop: 4 },
  totalsRow: { flexDirection: "row" },
  totalCard: {
    flex: 1,
    padding: 10,
    borderRadius: 8,
    backgroundColor: "#2A2735",
    alignItems: "flex-start",
  },
  totalLabel: { color: "#bbb", fontSize: 12, marginBottom: 4 },
  totalNumber: { color: "#fff", fontWeight: "700", fontSize: 16 },
  totalValue: { color: "#fff", fontWeight: "800", fontSize: 18 },
  plusText: { fontSize: 36, fontWeight: "800", color: "white" },
  addButton: {
    position: "absolute",
    right: 32,
    bottom: 32,
    width: 56,
    height: 56,
    borderRadius: 32,
    backgroundColor: "#7957DA",
    alignItems: "center",
    justifyContent: "center",
  },
});
